import { useQuery } from '@tanstack/react-query';
import { apiBaseURL, paymentsNamespace } from 'receivable-invoices-js/constants/hosts';
import { useSentry } from 'receivable-invoices-js/react/hooks/use-sentry';
import { shouldSendToSentry } from 'receivable-invoices-js/react/utils/network';

type PaymentStatus = 'open' | 'paid' | 'failed' | 'canceled' | 'expired';

interface Payment {
  status: PaymentStatus;
}

interface PaymentResponse {
  data: {
    attributes: Payment;
  };
}

/**
 * Polling status for payment
 * 'idle' - polling not started
 * 'polling' - currently polling
 * 'success' - payment successful
 * 'failure' - payment failed
 * 'timeout' - polling timed out / network failure
 */
export type PaymentPollingStatus = 'idle' | 'polling' | 'success' | 'failure' | 'timeout';

interface UseStatusPollingParams {
  paymentId: string | null;
  enabled?: boolean;
}

export function useStatusPolling({
  paymentId,
  enabled,
}: UseStatusPollingParams): PaymentPollingStatus {
  const sentry = useSentry();

  const { data, isError, failureReason, isFetching } = useQuery({
    queryKey: ['payment-polling', paymentId],
    queryFn: async () => {
      const response = await fetch(`${apiBaseURL}/${paymentsNamespace}/${paymentId}`, {
        method: 'GET',
      });

      if (!response.ok) {
        throw new Error('Failed to fetch payment');
      }

      const json = (await response.json()) as PaymentResponse;
      const payment = json.data.attributes;

      // Throw error for non-successful statuses to trigger retry
      if (['open', 'pending'].includes(payment.status)) {
        throw new Error('Payment still open');
      }

      return payment;
    },
    enabled,
    retry: 5,
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
  });

  // Send errors to Sentry
  if (isError && failureReason && shouldSendToSentry(failureReason)) {
    sentry.captureException(failureReason);
  }

  if (!paymentId) return 'idle';
  if (isFetching) return 'polling';
  if (isError) return 'timeout';
  if (data?.status === 'paid') return 'success';
  return 'failure';
}
